/**
 * WMS Warehouse Types
 * Shared between backend-wms, web-wms, and mobile-app
 *
 * Includes types for:
 * - Warehouse entity and DTOs
 * - Warehouse status management
 * - Area and capacity units
 * - Zone types
 *
 * DRY Compliance: Single source for all warehouse enums and types
 * Rule 15 Compliance: Proper type definitions
 */

// ============================================================================
// WAREHOUSE STATUS ENUMS
// ============================================================================

/**
 * Warehouse operational status
 */
export enum WarehouseStatus {
  OPERATIONAL = 'OPERATIONAL',
  MAINTENANCE = 'MAINTENANCE',
  CLOSED = 'CLOSED',
}

/**
 * Warehouse status labels
 */
export const WarehouseStatusLabels: Record<WarehouseStatus, string> = {
  [WarehouseStatus.OPERATIONAL]: 'Operational',
  [WarehouseStatus.MAINTENANCE]: 'Maintenance',
  [WarehouseStatus.CLOSED]: 'Closed',
};

/**
 * Warehouse status colors (Chakra UI colorPalette compatible)
 */
export const WarehouseStatusColors: Record<WarehouseStatus, string> = {
  [WarehouseStatus.OPERATIONAL]: 'green',
  [WarehouseStatus.MAINTENANCE]: 'orange',
  [WarehouseStatus.CLOSED]: 'red',
};

export const WAREHOUSE_STATUS_VALUES = ['OPERATIONAL', 'MAINTENANCE', 'CLOSED'] as const;

// ============================================================================
// AREA UNIT ENUMS
// ============================================================================

/**
 * Area measurement units
 */
export enum AreaUnit {
  SQM = 'sqm', // Square meters
  SQFT = 'sqft', // Square feet
}

/**
 * Area unit labels
 */
export const AreaUnitLabels: Record<AreaUnit, string> = {
  [AreaUnit.SQM]: 'Square Meters (m²)',
  [AreaUnit.SQFT]: 'Square Feet (ft²)',
};

/**
 * Area unit short labels
 */
export const AreaUnitShortLabels: Record<AreaUnit, string> = {
  [AreaUnit.SQM]: 'm²',
  [AreaUnit.SQFT]: 'ft²',
};

export const AREA_UNIT_VALUES = ['sqm', 'sqft'] as const;

// ============================================================================
// CAPACITY UNIT ENUMS
// ============================================================================

/**
 * Storage capacity units
 */
export enum CapacityUnit {
  PALLETS = 'pallets',
  CBM = 'cbm', // Cubic meters
  CFT = 'cft', // Cubic feet
}

/**
 * Capacity unit labels
 */
export const CapacityUnitLabels: Record<CapacityUnit, string> = {
  [CapacityUnit.PALLETS]: 'Pallets',
  [CapacityUnit.CBM]: 'Cubic Meters (m³)',
  [CapacityUnit.CFT]: 'Cubic Feet (ft³)',
};

/**
 * Capacity unit short labels
 */
export const CapacityUnitShortLabels: Record<CapacityUnit, string> = {
  [CapacityUnit.PALLETS]: 'pallets',
  [CapacityUnit.CBM]: 'm³',
  [CapacityUnit.CFT]: 'ft³',
};

export const CAPACITY_UNIT_VALUES = ['pallets', 'cbm', 'cft'] as const;

// ============================================================================
// ZONE TYPE ENUMS
// ============================================================================

/**
 * Warehouse zone types (distinct from layout-designer ZoneType)
 * Used for physical zone classification within warehouses
 */
export enum WarehouseZoneType {
  GENERAL = 'GENERAL',
  COLD_STORAGE = 'COLD_STORAGE',
  FROZEN = 'FROZEN',
  HAZMAT = 'HAZMAT',
  HIGH_VALUE = 'HIGH_VALUE',
  RETURNS = 'RETURNS',
  STAGING = 'STAGING',
  CROSS_DOCK = 'CROSS_DOCK',
  QUARANTINE = 'QUARANTINE',
  QC_HOLD = 'QC_HOLD',
  BULK = 'BULK',
  PICKING = 'PICKING',
  OVERFLOW = 'OVERFLOW',
  SHIPPING = 'SHIPPING',
  RECEIVING = 'RECEIVING',
}

/**
 * Warehouse zone type labels
 */
export const WarehouseZoneTypeLabels: Record<WarehouseZoneType, string> = {
  [WarehouseZoneType.GENERAL]: 'General Storage',
  [WarehouseZoneType.COLD_STORAGE]: 'Cold Storage',
  [WarehouseZoneType.FROZEN]: 'Frozen Storage',
  [WarehouseZoneType.HAZMAT]: 'Hazardous Materials',
  [WarehouseZoneType.HIGH_VALUE]: 'High Value',
  [WarehouseZoneType.RETURNS]: 'Returns Processing',
  [WarehouseZoneType.STAGING]: 'Staging Area',
  [WarehouseZoneType.CROSS_DOCK]: 'Cross-Dock',
  [WarehouseZoneType.QUARANTINE]: 'Quarantine',
  [WarehouseZoneType.QC_HOLD]: 'QC Hold',
  [WarehouseZoneType.BULK]: 'Bulk Storage',
  [WarehouseZoneType.PICKING]: 'Picking Area',
  [WarehouseZoneType.OVERFLOW]: 'Overflow Storage',
  [WarehouseZoneType.SHIPPING]: 'Shipping Area',
  [WarehouseZoneType.RECEIVING]: 'Receiving Area',
};

/**
 * Warehouse zone type colors (Chakra UI colorPalette compatible)
 */
export const WarehouseZoneTypeColors: Record<WarehouseZoneType, string> = {
  [WarehouseZoneType.GENERAL]: 'gray',
  [WarehouseZoneType.COLD_STORAGE]: 'cyan',
  [WarehouseZoneType.FROZEN]: 'blue',
  [WarehouseZoneType.HAZMAT]: 'red',
  [WarehouseZoneType.HIGH_VALUE]: 'purple',
  [WarehouseZoneType.RETURNS]: 'orange',
  [WarehouseZoneType.STAGING]: 'yellow',
  [WarehouseZoneType.CROSS_DOCK]: 'green',
  [WarehouseZoneType.QUARANTINE]: 'red',
  [WarehouseZoneType.QC_HOLD]: 'orange',
  [WarehouseZoneType.BULK]: 'teal',
  [WarehouseZoneType.PICKING]: 'green',
  [WarehouseZoneType.OVERFLOW]: 'gray',
  [WarehouseZoneType.SHIPPING]: 'blue',
  [WarehouseZoneType.RECEIVING]: 'purple',
};

export const WAREHOUSE_ZONE_TYPE_VALUES = [
  'GENERAL',
  'COLD_STORAGE',
  'FROZEN',
  'HAZMAT',
  'HIGH_VALUE',
  'RETURNS',
  'STAGING',
  'CROSS_DOCK',
  'QUARANTINE',
  'QC_HOLD',
  'BULK',
  'PICKING',
  'OVERFLOW',
  'SHIPPING',
  'RECEIVING',
] as const;

// ============================================================================
// AISLE TYPE ENUMS
// ============================================================================

/**
 * Aisle types for warehouse aisle classification
 */
export enum AisleType {
  STANDARD = 'STANDARD',
  NARROW = 'NARROW',
  WIDE = 'WIDE',
  DRIVE_IN = 'DRIVE_IN',
  DRIVE_THROUGH = 'DRIVE_THROUGH',
}

/**
 * Aisle type labels for UI display
 */
export const AisleTypeLabels: Record<AisleType, string> = {
  [AisleType.STANDARD]: 'Standard',
  [AisleType.NARROW]: 'Narrow',
  [AisleType.WIDE]: 'Wide',
  [AisleType.DRIVE_IN]: 'Drive-In',
  [AisleType.DRIVE_THROUGH]: 'Drive-Through',
};

export const AISLE_TYPE_VALUES = Object.values(AisleType) as string[];

// ============================================================================
// LOCATION STATUS ENUMS
// ============================================================================

/**
 * Location operational status
 * DRY Compliance: Single source of truth for location status across backend/frontend
 */
export enum LocationStatus {
  AVAILABLE = 'AVAILABLE',
  OCCUPIED = 'OCCUPIED',
  RESERVED = 'RESERVED',
  BLOCKED = 'BLOCKED',
  MAINTENANCE = 'MAINTENANCE',
}

/**
 * Location status labels for UI display
 */
export const LocationStatusLabels: Record<LocationStatus, string> = {
  [LocationStatus.AVAILABLE]: 'Available',
  [LocationStatus.OCCUPIED]: 'Occupied',
  [LocationStatus.RESERVED]: 'Reserved',
  [LocationStatus.BLOCKED]: 'Blocked',
  [LocationStatus.MAINTENANCE]: 'Maintenance',
};

/**
 * Location status colors (Chakra UI colorPalette compatible)
 */
export const LocationStatusColors: Record<LocationStatus, string> = {
  [LocationStatus.AVAILABLE]: 'green',
  [LocationStatus.OCCUPIED]: 'blue',
  [LocationStatus.RESERVED]: 'yellow',
  [LocationStatus.BLOCKED]: 'red',
  [LocationStatus.MAINTENANCE]: 'orange',
};

export const LOCATION_STATUS_VALUES = Object.values(LocationStatus) as string[];

// ============================================================================
// STORAGE TYPE ENUMS (Location Type Classification)
// ============================================================================

/**
 * Storage type classification for location types
 * Used for putaway/picking business rules and equipment compatibility
 *
 * Business Rules:
 * - PALLET: Forklift-accessible locations for palletized goods
 * - SHELF: Manual picking locations for small items
 * - FLOOR: Ground-level bulk storage
 * - RACK: Multi-level rack systems for high-density storage
 * - BIN: Small compartment storage for parts/components
 * - BULK: Reserve/overflow storage areas
 */
export enum StorageType {
  PALLET = 'PALLET',
  SHELF = 'SHELF',
  FLOOR = 'FLOOR',
  RACK = 'RACK',
  BIN = 'BIN',
  BULK = 'BULK',
  STAGING = 'STAGING', // Receiving/shipping staging areas
}

/**
 * Storage type labels for UI display
 */
export const StorageTypeLabels: Record<StorageType, string> = {
  [StorageType.PALLET]: 'Pallet Storage',
  [StorageType.SHELF]: 'Shelf Storage',
  [StorageType.FLOOR]: 'Floor Storage',
  [StorageType.RACK]: 'Rack Storage',
  [StorageType.BIN]: 'Bin Storage',
  [StorageType.BULK]: 'Bulk Storage',
  [StorageType.STAGING]: 'Staging Area',
};

/**
 * Storage type descriptions for UI tooltips/help
 */
export const StorageTypeDescriptions: Record<StorageType, string> = {
  [StorageType.PALLET]: 'Forklift-accessible locations for palletized goods',
  [StorageType.SHELF]: 'Manual picking locations for small/medium items',
  [StorageType.FLOOR]: 'Ground-level storage for heavy or bulky items',
  [StorageType.RACK]: 'Multi-level rack systems for high-density pallet storage',
  [StorageType.BIN]: 'Small compartment storage for parts and components',
  [StorageType.BULK]: 'Reserve/overflow storage areas, typically for replenishment',
  [StorageType.STAGING]: 'Temporary holding areas for receiving or shipping',
};

/**
 * Storage types suitable for picking operations
 */
export const PICKABLE_STORAGE_TYPES: StorageType[] = [
  StorageType.SHELF,
  StorageType.BIN,
  StorageType.PALLET,
  StorageType.RACK,
];

/**
 * Storage types suitable for bulk/reserve storage
 */
export const BULK_STORAGE_TYPES: StorageType[] = [
  StorageType.BULK,
  StorageType.FLOOR,
  StorageType.RACK,
];

export const STORAGE_TYPE_VALUES = Object.values(StorageType) as string[];

// ============================================================================
// LOCATION TYPE DEFAULTS INTERFACE
// ============================================================================

/**
 * Default values inherited from Location Type when creating a Location
 * DRY Compliance: Single source of truth for location type defaults
 * Rule 15: Applied at service layer, not duplicated in frontend
 */
export interface LocationTypeDefaults {
  /** Whether location can be used for picking operations */
  is_pickable: boolean;
  /** Whether items can be stacked in this location */
  is_stackable: boolean;
  /** Maximum weight capacity (in weight_unit) */
  max_weight?: number;
  /** Weight unit (kg, lb) */
  weight_unit?: string;
  /** Maximum volume capacity (in volume_unit) */
  max_volume?: number;
  /** Volume unit (cbm, cft) */
  volume_unit?: string;
  /** Maximum units capacity */
  max_units?: number;
  /** Units unit (EA, CASE, PALLET) */
  units_unit?: string;
  /** Maximum dimensions (width, length, height) */
  max_width?: number;
  max_length?: number;
  max_height?: number;
  /** Dimension unit (m, ft) */
  dimension_unit?: string;
  /** Storage type classification */
  storage_type: StorageType;
}

// ============================================================================
// DIMENSION UNIT ENUMS
// ============================================================================

/**
 * Dimension units for warehouse measurements
 */
export enum DimensionUnit {
  METERS = 'm',
  FEET = 'ft',
}

/**
 * Dimension unit labels for UI display
 */
export const DimensionUnitLabels: Record<DimensionUnit, string> = {
  [DimensionUnit.METERS]: 'Meters (m)',
  [DimensionUnit.FEET]: 'Feet (ft)',
};

export const DIMENSION_UNIT_VALUES = Object.values(DimensionUnit) as string[];

// ============================================================================
// AISLE DIMENSION THRESHOLDS
// ============================================================================

/**
 * Dimension thresholds for automatic aisle type classification
 * Based on industry standards for forklift operation safety
 *
 * NARROW (VNA): < 8ft / 2.4m - Requires turret truck or order picker
 * STANDARD: 8-12ft / 2.4-3.6m - Standard forklifts can operate
 * WIDE: > 12ft / 3.6m - All equipment types compatible
 */
export const AISLE_WIDTH_THRESHOLDS = {
  [DimensionUnit.FEET]: {
    NARROW_MAX: 8, // < 8ft = NARROW
    STANDARD_MAX: 12, // 8-12ft = STANDARD
    // > 12ft = WIDE
  },
  [DimensionUnit.METERS]: {
    NARROW_MAX: 2.4, // < 2.4m = NARROW
    STANDARD_MAX: 3.6, // 2.4-3.6m = STANDARD
    // > 3.6m = WIDE
  },
} as const;

/**
 * Minimum recommended aisle dimensions by type
 */
export const AISLE_MIN_DIMENSIONS: Record<
  AisleType,
  {
    width: { ft: number; m: number };
    height: { ft: number; m: number };
  }
> = {
  [AisleType.NARROW]: {
    width: { ft: 6, m: 1.8 },
    height: { ft: 20, m: 6 },
  },
  [AisleType.STANDARD]: {
    width: { ft: 8, m: 2.4 },
    height: { ft: 25, m: 7.5 },
  },
  [AisleType.WIDE]: {
    width: { ft: 12, m: 3.6 },
    height: { ft: 30, m: 9 },
  },
  [AisleType.DRIVE_IN]: {
    width: { ft: 10, m: 3 },
    height: { ft: 20, m: 6 },
  },
  [AisleType.DRIVE_THROUGH]: {
    width: { ft: 10, m: 3 },
    height: { ft: 20, m: 6 },
  },
};

// ============================================================================
// AISLE HEIGHT & LENGTH LIMITS
// ============================================================================

/**
 * Maximum aisle height limits by type
 * Based on typical warehouse ceiling heights and equipment reach
 */
export const AISLE_HEIGHT_LIMITS = {
  [DimensionUnit.FEET]: {
    MIN: 8, // Minimum usable height
    STANDARD_MAX: 30, // Standard warehouse ceiling
    HIGH_BAY_MAX: 45, // High-bay warehouse
  },
  [DimensionUnit.METERS]: {
    MIN: 2.4,
    STANDARD_MAX: 9,
    HIGH_BAY_MAX: 14,
  },
} as const;

/**
 * Aisle length limits
 * Based on forklift travel efficiency and safety
 */
export const AISLE_LENGTH_LIMITS = {
  [DimensionUnit.FEET]: {
    MIN: 20, // Minimum practical aisle length
    RECOMMENDED_MAX: 200, // Optimal for forklift efficiency
    ABSOLUTE_MAX: 500, // Maximum with safety considerations
  },
  [DimensionUnit.METERS]: {
    MIN: 6,
    RECOMMENDED_MAX: 60,
    ABSOLUTE_MAX: 150,
  },
} as const;

/**
 * Levels and Bays limits
 * Based on structural and operational constraints
 */
export const AISLE_LEVELS_LIMITS = {
  MIN: 1,
  RECOMMENDED_MAX: 6, // Most common rack configurations
  ABSOLUTE_MAX: 12, // High-bay warehouses
} as const;

export const AISLE_BAYS_LIMITS = {
  MIN: 1,
  RECOMMENDED_MAX: 50, // Optimal for picking efficiency
  ABSOLUTE_MAX: 100, // Maximum manageable
} as const;

/**
 * Calculate maximum location capacity for an aisle
 * Formula: levels × bays (simplified, actual depends on rack configuration)
 *
 * @param levels - Number of vertical levels
 * @param bays - Number of horizontal bays
 * @returns Maximum location count
 */
export function calculateLocationCapacity(levels: number, bays: number): number {
  if (levels < 1 || bays < 1) return 0;
  return levels * bays;
}

/**
 * Validate aisle height against equipment reach
 *
 * @param height - Aisle height
 * @param aisleType - Aisle type
 * @param unit - Unit of measurement
 * @returns Validation result with warnings
 */
export function validateAisleHeight(
  height: number,
  aisleType: AisleType,
  unit: DimensionUnit = DimensionUnit.FEET
): {
  valid: boolean;
  warnings: string[];
  exceedsStandard: boolean;
  compatibleEquipment: EquipmentType[];
} {
  const warnings: string[] = [];
  const limits = AISLE_HEIGHT_LIMITS[unit];
  const minDimensions = AISLE_MIN_DIMENSIONS[aisleType];
  const minHeight = unit === DimensionUnit.FEET ? minDimensions.height.ft : minDimensions.height.m;

  let exceedsStandard = false;

  // Check minimum height
  if (height < limits.MIN) {
    warnings.push(`Height ${height}${unit} is below minimum ${limits.MIN}${unit}`);
  }

  // Check against aisle type recommendation
  if (height < minHeight) {
    warnings.push(
      `Height ${height}${unit} is below recommended ${minHeight}${unit} for ${AisleTypeLabels[aisleType]} aisle`
    );
  }

  // Check if exceeds standard warehouse height
  if (height > limits.STANDARD_MAX) {
    exceedsStandard = true;
    warnings.push(
      `Height ${height}${unit} exceeds standard warehouse ceiling (${limits.STANDARD_MAX}${unit}). Ensure building can accommodate.`
    );
  }

  // Check absolute maximum
  if (height > limits.HIGH_BAY_MAX) {
    warnings.push(
      `Height ${height}${unit} exceeds high-bay warehouse maximum. Special equipment required.`
    );
  }

  // Find equipment that can reach this height
  const compatibleEquipment = getEquipmentByReachHeight(height, unit);

  if (compatibleEquipment.length === 0 && height > limits.MIN) {
    warnings.push('No standard equipment can reach this height');
  }

  return {
    valid: height >= limits.MIN,
    warnings,
    exceedsStandard,
    compatibleEquipment,
  };
}

/**
 * Validate aisle length
 *
 * @param length - Aisle length
 * @param unit - Unit of measurement
 * @returns Validation result with warnings
 */
export function validateAisleLength(
  length: number,
  unit: DimensionUnit = DimensionUnit.FEET
): {
  valid: boolean;
  warnings: string[];
  exceedsRecommended: boolean;
} {
  const warnings: string[] = [];
  const limits = AISLE_LENGTH_LIMITS[unit];
  let exceedsRecommended = false;

  if (length < limits.MIN) {
    warnings.push(`Length ${length}${unit} is below minimum ${limits.MIN}${unit}`);
  }

  if (length > limits.RECOMMENDED_MAX) {
    exceedsRecommended = true;
    warnings.push(
      `Length ${length}${unit} exceeds recommended ${limits.RECOMMENDED_MAX}${unit}. Consider splitting into multiple aisles for efficiency.`
    );
  }

  if (length > limits.ABSOLUTE_MAX) {
    warnings.push(`Length ${length}${unit} exceeds absolute maximum ${limits.ABSOLUTE_MAX}${unit}`);
  }

  return {
    valid: length >= limits.MIN && length <= limits.ABSOLUTE_MAX,
    warnings,
    exceedsRecommended,
  };
}

/**
 * Validate levels and bays configuration
 *
 * @param levels - Number of levels
 * @param bays - Number of bays
 * @returns Validation result with warnings and capacity info
 */
export function validateLevelsAndBays(
  levels: number,
  bays: number
): {
  valid: boolean;
  warnings: string[];
  locationCapacity: number;
  capacityLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH';
} {
  const warnings: string[] = [];

  // Validate levels
  if (levels < AISLE_LEVELS_LIMITS.MIN) {
    warnings.push(`Levels must be at least ${AISLE_LEVELS_LIMITS.MIN}`);
  }
  if (levels > AISLE_LEVELS_LIMITS.RECOMMENDED_MAX) {
    warnings.push(
      `Levels (${levels}) exceeds recommended maximum (${AISLE_LEVELS_LIMITS.RECOMMENDED_MAX}). High-reach equipment required.`
    );
  }
  if (levels > AISLE_LEVELS_LIMITS.ABSOLUTE_MAX) {
    warnings.push(
      `Levels (${levels}) exceeds absolute maximum (${AISLE_LEVELS_LIMITS.ABSOLUTE_MAX})`
    );
  }

  // Validate bays
  if (bays < AISLE_BAYS_LIMITS.MIN) {
    warnings.push(`Bays must be at least ${AISLE_BAYS_LIMITS.MIN}`);
  }
  if (bays > AISLE_BAYS_LIMITS.RECOMMENDED_MAX) {
    warnings.push(
      `Bays (${bays}) exceeds recommended maximum (${AISLE_BAYS_LIMITS.RECOMMENDED_MAX}). Long travel distances may reduce efficiency.`
    );
  }
  if (bays > AISLE_BAYS_LIMITS.ABSOLUTE_MAX) {
    warnings.push(`Bays (${bays}) exceeds absolute maximum (${AISLE_BAYS_LIMITS.ABSOLUTE_MAX})`);
  }

  // Calculate capacity
  const locationCapacity = calculateLocationCapacity(Math.max(1, levels), Math.max(1, bays));

  // Determine capacity level
  let capacityLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH';
  if (locationCapacity <= 50) {
    capacityLevel = 'LOW';
  } else if (locationCapacity <= 150) {
    capacityLevel = 'MEDIUM';
  } else if (locationCapacity <= 300) {
    capacityLevel = 'HIGH';
  } else {
    capacityLevel = 'VERY_HIGH';
  }

  const valid =
    levels >= AISLE_LEVELS_LIMITS.MIN &&
    levels <= AISLE_LEVELS_LIMITS.ABSOLUTE_MAX &&
    bays >= AISLE_BAYS_LIMITS.MIN &&
    bays <= AISLE_BAYS_LIMITS.ABSOLUTE_MAX;

  return {
    valid,
    warnings,
    locationCapacity,
    capacityLevel,
  };
}

/**
 * Get equipment that can reach a specific height
 *
 * @param height - Required reach height
 * @param unit - Unit of measurement
 * @returns Array of compatible equipment types
 */
export function getEquipmentByReachHeight(
  height: number,
  unit: DimensionUnit = DimensionUnit.FEET
): EquipmentType[] {
  // Convert to feet for comparison
  const heightFt = unit === DimensionUnit.METERS ? height * 3.28084 : height;

  // Equipment max reach heights (in feet)
  const equipmentReach: Record<EquipmentType, number> = {
    [EquipmentType.PALLET_JACK]: 8, // Floor level only
    [EquipmentType.ORDER_PICKER]: 32, // Up to 32 ft
    [EquipmentType.COUNTER_BALANCE]: 15, // Up to 15 ft
    [EquipmentType.REACH_TRUCK]: 40, // Up to 40 ft
    [EquipmentType.TURRET_TRUCK]: 50, // Up to 50 ft (VNA)
    [EquipmentType.SIDE_LOADER]: 25, // Up to 25 ft
    [EquipmentType.FORKLIFT_TELESCOPIC]: 55, // Up to 55 ft
  };

  return Object.entries(equipmentReach)
    .filter(([, maxReach]) => heightFt <= maxReach)
    .map(([type]) => type as EquipmentType);
}

/**
 * Determine recommended aisle type based on width
 *
 * @param width - Aisle width
 * @param unit - Unit of measurement (default: feet)
 * @returns Recommended AisleType
 *
 * @example
 * getAisleTypeFromWidth(7, DimensionUnit.FEET)   // AisleType.NARROW
 * getAisleTypeFromWidth(10, DimensionUnit.FEET)  // AisleType.STANDARD
 * getAisleTypeFromWidth(15, DimensionUnit.FEET)  // AisleType.WIDE
 * getAisleTypeFromWidth(3, DimensionUnit.METERS) // AisleType.STANDARD
 */
export function getAisleTypeFromWidth(
  width: number,
  unit: DimensionUnit = DimensionUnit.FEET
): AisleType {
  const thresholds = AISLE_WIDTH_THRESHOLDS[unit];

  if (width < thresholds.NARROW_MAX) {
    return AisleType.NARROW;
  } else if (width <= thresholds.STANDARD_MAX) {
    return AisleType.STANDARD;
  } else {
    return AisleType.WIDE;
  }
}

/**
 * Check if a dimension change affects aisle type classification
 *
 * @param oldWidth - Previous width
 * @param newWidth - New width
 * @param unit - Unit of measurement
 * @returns Object with change analysis
 */
export function analyzeWidthChange(
  oldWidth: number | null | undefined,
  newWidth: number | null | undefined,
  unit: DimensionUnit = DimensionUnit.FEET
): {
  oldType: AisleType | null;
  newType: AisleType | null;
  typeChanged: boolean;
  requiresConfirmation: boolean;
  incompatibleEquipment: EquipmentType[];
} {
  const oldType = oldWidth != null ? getAisleTypeFromWidth(oldWidth, unit) : null;
  const newType = newWidth != null ? getAisleTypeFromWidth(newWidth, unit) : null;

  const typeChanged = oldType !== null && newType !== null && oldType !== newType;

  // Find equipment that was compatible but no longer is
  let incompatibleEquipment: EquipmentType[] = [];
  if (typeChanged && oldType && newType) {
    const oldCompatible = getCompatibleEquipment(oldType);
    const newCompatible = getCompatibleEquipment(newType);
    incompatibleEquipment = oldCompatible.filter((e) => !newCompatible.includes(e));
  }

  return {
    oldType,
    newType,
    typeChanged,
    requiresConfirmation: incompatibleEquipment.length > 0,
    incompatibleEquipment,
  };
}

/**
 * Get equipment that would become incompatible if aisle type changes
 *
 * @param fromType - Current aisle type
 * @param toType - New aisle type
 * @returns Array of equipment types that would no longer be compatible
 */
export function getEquipmentIncompatibleWithTypeChange(
  fromType: AisleType,
  toType: AisleType
): EquipmentType[] {
  const fromCompatible = getCompatibleEquipment(fromType);
  const toCompatible = getCompatibleEquipment(toType);

  return fromCompatible.filter((e) => !toCompatible.includes(e));
}

/**
 * Validate dimension change and return warnings
 *
 * @param params - Dimension change parameters
 * @returns Validation result with warnings
 */
export function validateAisleDimensionChange(params: {
  currentWidth?: number | null;
  newWidth?: number | null;
  currentAisleType: AisleType;
  unit?: DimensionUnit;
  hasActiveTasks?: boolean;
}): {
  valid: boolean;
  warnings: string[];
  errors: string[];
  suggestedAisleType: AisleType | null;
  equipmentWarnings: string[];
} {
  const {
    currentWidth,
    newWidth,
    currentAisleType,
    unit = DimensionUnit.FEET,
    hasActiveTasks = false,
  } = params;

  const warnings: string[] = [];
  const errors: string[] = [];
  const equipmentWarnings: string[] = [];

  // Check for active tasks
  if (hasActiveTasks) {
    warnings.push(
      'This aisle has active tasks. Consider completing them before changing dimensions.'
    );
  }

  // Analyze width change
  const analysis = analyzeWidthChange(currentWidth, newWidth, unit);

  // Check if aisle type would change
  if (analysis.typeChanged) {
    warnings.push(
      `Width change would reclassify aisle from ${AisleTypeLabels[analysis.oldType!]} to ${AisleTypeLabels[analysis.newType!]}`
    );

    // Check equipment compatibility
    if (analysis.incompatibleEquipment.length > 0) {
      const equipmentNames = analysis.incompatibleEquipment
        .map((e) => EquipmentTypeLabels[e])
        .join(', ');

      equipmentWarnings.push(
        `The following equipment can no longer operate in this aisle: ${equipmentNames}`
      );

      warnings.push('Equipment compatibility will be affected by this change.');
    }
  }

  // Validate minimum dimensions
  if (newWidth != null && newWidth > 0) {
    const minDimensions = AISLE_MIN_DIMENSIONS[currentAisleType];
    const minWidth = unit === DimensionUnit.FEET ? minDimensions.width.ft : minDimensions.width.m;

    if (newWidth < minWidth) {
      warnings.push(
        `Width ${newWidth}${unit} is below recommended minimum ${minWidth}${unit} for ${AisleTypeLabels[currentAisleType]} aisle`
      );
    }
  }

  return {
    valid: errors.length === 0,
    warnings,
    errors,
    suggestedAisleType: analysis.newType,
    equipmentWarnings,
  };
}

// ============================================================================
// EQUIPMENT TYPE ENUMS
// ============================================================================

/**
 * Equipment types for warehouse operations
 * Used for equipment-aisle compatibility validation
 */
export enum EquipmentType {
  /** Standard counter-balance forklift */
  COUNTER_BALANCE = 'COUNTER_BALANCE',
  /** Reach truck for standard/wide aisles */
  REACH_TRUCK = 'REACH_TRUCK',
  /** Turret truck for very narrow aisles (VNA) */
  TURRET_TRUCK = 'TURRET_TRUCK',
  /** Low-level order picker */
  ORDER_PICKER = 'ORDER_PICKER',
  /** Manual/hand pallet jack */
  PALLET_JACK = 'PALLET_JACK',
  /** Side loader for long items */
  SIDE_LOADER = 'SIDE_LOADER',
  /** Telescopic forklift for high bays */
  FORKLIFT_TELESCOPIC = 'FORKLIFT_TELESCOPIC',
}

/**
 * Equipment type labels for UI display
 */
export const EquipmentTypeLabels: Record<EquipmentType, string> = {
  [EquipmentType.COUNTER_BALANCE]: 'Counter-Balance Forklift',
  [EquipmentType.REACH_TRUCK]: 'Reach Truck',
  [EquipmentType.TURRET_TRUCK]: 'Turret Truck (VNA)',
  [EquipmentType.ORDER_PICKER]: 'Order Picker',
  [EquipmentType.PALLET_JACK]: 'Pallet Jack',
  [EquipmentType.SIDE_LOADER]: 'Side Loader',
  [EquipmentType.FORKLIFT_TELESCOPIC]: 'Telescopic Forklift',
};

export const EQUIPMENT_TYPE_VALUES = Object.values(EquipmentType) as string[];

// ============================================================================
// EQUIPMENT - AISLE COMPATIBILITY MATRIX
// ============================================================================

/**
 * Equipment compatibility with aisle types
 * Defines which equipment can safely operate in which aisle types
 *
 * Business Rule: Equipment can only be assigned to tasks in compatible aisles
 */
export const EQUIPMENT_AISLE_COMPATIBILITY: Record<EquipmentType, AisleType[]> = {
  [EquipmentType.COUNTER_BALANCE]: [
    AisleType.STANDARD,
    AisleType.WIDE,
    AisleType.DRIVE_IN,
    AisleType.DRIVE_THROUGH,
  ],
  [EquipmentType.REACH_TRUCK]: [
    AisleType.STANDARD,
    AisleType.WIDE,
    AisleType.DRIVE_IN,
    AisleType.DRIVE_THROUGH,
  ],
  [EquipmentType.TURRET_TRUCK]: [
    AisleType.NARROW, // VNA - Very Narrow Aisle
    AisleType.STANDARD,
  ],
  [EquipmentType.ORDER_PICKER]: [AisleType.STANDARD, AisleType.NARROW, AisleType.WIDE],
  [EquipmentType.PALLET_JACK]: [AisleType.STANDARD, AisleType.WIDE],
  [EquipmentType.SIDE_LOADER]: [AisleType.WIDE],
  [EquipmentType.FORKLIFT_TELESCOPIC]: [AisleType.STANDARD, AisleType.WIDE],
};

/**
 * Check if equipment is compatible with aisle type
 *
 * @param equipmentType - Type of equipment
 * @param aisleType - Type of aisle
 * @returns true if equipment can operate in this aisle type
 *
 * @example
 * isEquipmentCompatible(EquipmentType.TURRET_TRUCK, AisleType.NARROW) // true
 * isEquipmentCompatible(EquipmentType.PALLET_JACK, AisleType.NARROW) // false
 */
export function isEquipmentCompatible(equipmentType: EquipmentType, aisleType: AisleType): boolean {
  const compatibleAisles = EQUIPMENT_AISLE_COMPATIBILITY[equipmentType];
  return compatibleAisles?.includes(aisleType) ?? false;
}

/**
 * Get all equipment types compatible with a specific aisle type
 *
 * @param aisleType - Type of aisle
 * @returns Array of compatible equipment types
 *
 * @example
 * getCompatibleEquipment(AisleType.NARROW)
 * // Returns: [TURRET_TRUCK, ORDER_PICKER]
 */
export function getCompatibleEquipment(aisleType: AisleType): EquipmentType[] {
  return Object.entries(EQUIPMENT_AISLE_COMPATIBILITY)
    .filter(([, aisleTypes]) => aisleTypes.includes(aisleType))
    .map(([equipmentType]) => equipmentType as EquipmentType);
}

// ============================================================================
// AISLE TYPE EFFICIENCY FACTORS
// ============================================================================

/**
 * Product velocity classification for slotting
 * A = High velocity (fast movers)
 * B = Medium velocity
 * C = Low velocity (slow movers)
 */
export type ProductVelocityClass = 'A' | 'B' | 'C';

/**
 * Efficiency factors by aisle type
 * Used for capacity calculations, slotting optimization, and time estimates
 *
 * storage_density: Higher = more products per square meter
 * pick_efficiency: Higher = faster picking operations
 * putaway_time_factor: Multiplier for base putaway time (1.0 = standard)
 * space_utilization: Percentage of space effectively used
 * recommended_for_velocity: Best suited product velocity class
 */
export interface AisleTypeEfficiency {
  /** Storage density factor (0-1, higher = denser storage) */
  storage_density: number;
  /** Picking efficiency factor (0-1, higher = faster picking) */
  pick_efficiency: number;
  /** Average putaway time multiplier (1.0 = standard time) */
  putaway_time_factor: number;
  /** Space utilization percentage (0-1) */
  space_utilization: number;
  /** Recommended for this product velocity class */
  recommended_for_velocity: ProductVelocityClass | 'ANY';
}

/**
 * Efficiency factors for each aisle type
 *
 * Business Rules:
 * - WIDE aisles: Fastest picking, best for high-velocity (A-class) products
 * - NARROW aisles: High density, slower picking, good for medium-velocity (B-class)
 * - DRIVE_IN: Highest density, LIFO access, best for low-velocity (C-class)
 * - DRIVE_THROUGH: High density, FIFO access, good for low-velocity (C-class)
 * - STANDARD: Balanced, suitable for any velocity
 */
export const AISLE_TYPE_EFFICIENCY: Record<AisleType, AisleTypeEfficiency> = {
  [AisleType.STANDARD]: {
    storage_density: 0.7,
    pick_efficiency: 0.85,
    putaway_time_factor: 1.0,
    space_utilization: 0.75,
    recommended_for_velocity: 'ANY',
  },
  [AisleType.NARROW]: {
    storage_density: 0.9,
    pick_efficiency: 0.7,
    putaway_time_factor: 1.2,
    space_utilization: 0.9,
    recommended_for_velocity: 'B',
  },
  [AisleType.WIDE]: {
    storage_density: 0.55,
    pick_efficiency: 0.95,
    putaway_time_factor: 0.8,
    space_utilization: 0.6,
    recommended_for_velocity: 'A',
  },
  [AisleType.DRIVE_IN]: {
    storage_density: 0.85,
    pick_efficiency: 0.5, // LIFO - slower access
    putaway_time_factor: 1.3,
    space_utilization: 0.85,
    recommended_for_velocity: 'C',
  },
  [AisleType.DRIVE_THROUGH]: {
    storage_density: 0.8,
    pick_efficiency: 0.6, // FIFO - moderate access
    putaway_time_factor: 1.2,
    space_utilization: 0.8,
    recommended_for_velocity: 'C',
  },
};

// ============================================================================
// AISLE TYPE HELPER FUNCTIONS
// ============================================================================

/**
 * Get effective storage capacity considering aisle type efficiency
 *
 * @param physicalCapacity - Physical storage capacity (e.g., number of pallets)
 * @param aisleType - Type of aisle
 * @returns Effective usable capacity
 *
 * @example
 * getEffectiveCapacity(100, AisleType.NARROW) // Returns: 90 (90% density)
 * getEffectiveCapacity(100, AisleType.WIDE) // Returns: 55 (55% density)
 */
export function getEffectiveCapacity(physicalCapacity: number, aisleType: AisleType): number {
  const efficiency = AISLE_TYPE_EFFICIENCY[aisleType];
  return Math.floor(physicalCapacity * efficiency.storage_density);
}

/**
 * Get estimated pick time for a location based on aisle type
 *
 * @param baseTime - Base pick time in seconds/minutes
 * @param aisleType - Type of aisle
 * @returns Estimated pick time adjusted for aisle type
 *
 * @example
 * getEstimatedPickTime(60, AisleType.WIDE) // Returns: 63 (95% efficiency)
 * getEstimatedPickTime(60, AisleType.DRIVE_IN) // Returns: 120 (50% efficiency)
 */
export function getEstimatedPickTime(baseTime: number, aisleType: AisleType): number {
  const efficiency = AISLE_TYPE_EFFICIENCY[aisleType];
  return Math.ceil(baseTime / efficiency.pick_efficiency);
}

/**
 * Get recommended aisle types for a product based on velocity class
 *
 * @param velocity - Product velocity class (A/B/C)
 * @param availableAisleTypes - Aisle types available in the warehouse
 * @returns Array of recommended aisle types, sorted by suitability (best first)
 *
 * @example
 * getRecommendedAisleTypesForVelocity('A', [STANDARD, WIDE, NARROW])
 * // Returns: [WIDE, STANDARD] (WIDE best for high-velocity)
 */
export function getRecommendedAisleTypesForVelocity(
  velocity: ProductVelocityClass,
  availableAisleTypes: AisleType[]
): AisleType[] {
  // Score each available aisle type
  const scored = availableAisleTypes.map((aisleType) => {
    const efficiency = AISLE_TYPE_EFFICIENCY[aisleType];
    let score = 0;

    // Exact velocity match gets highest score
    if (efficiency.recommended_for_velocity === velocity) {
      score += 100;
    } else if (efficiency.recommended_for_velocity === 'ANY') {
      score += 50;
    }

    // Add pick efficiency bonus (important for picking operations)
    score += efficiency.pick_efficiency * 50;

    // Add space utilization bonus (important for storage efficiency)
    score += efficiency.space_utilization * 30;

    return { aisleType, score };
  });

  // Sort by score descending (best first)
  scored.sort((a, b) => b.score - a.score);

  return scored.map((s) => s.aisleType);
}

/**
 * Calculate slotting score for a location based on aisle type and product velocity
 *
 * @param aisleType - Type of aisle
 * @param productVelocity - Product velocity class
 * @returns Score (higher = better match for slotting)
 *
 * @example
 * calculateSlottingScore(AisleType.WIDE, 'A') // Returns: 145 (excellent match)
 * calculateSlottingScore(AisleType.DRIVE_IN, 'A') // Returns: 65 (poor match)
 */
export function calculateSlottingScore(
  aisleType: AisleType,
  productVelocity: ProductVelocityClass
): number {
  const efficiency = AISLE_TYPE_EFFICIENCY[aisleType];
  let score = 0;

  // Velocity matching
  if (efficiency.recommended_for_velocity === productVelocity) {
    score += 100;
  } else if (efficiency.recommended_for_velocity === 'ANY') {
    score += 50;
  }

  // Pick efficiency contribution
  score += efficiency.pick_efficiency * 50;

  return Math.round(score);
}

// ============================================================================
// LOCATION CODE TEMPLATE CONSTANTS
// ============================================================================

/**
 * Default location code template
 */
export const DEFAULT_LOCATION_CODE_TEMPLATE = '{WAREHOUSE_CODE}-{AISLE_CODE}-L{LEVEL}-P{POSITION}';

/**
 * Location code template variables
 */
export const LOCATION_CODE_VARIABLES = [
  { key: 'WAREHOUSE_CODE', description: 'Warehouse code', example: 'WH001' },
  { key: 'ZONE_CODE', description: 'Zone code', example: 'A' },
  { key: 'AISLE_CODE', description: 'Aisle code', example: '01' },
  { key: 'LEVEL', description: 'Level/Rack level', example: '1' },
  { key: 'POSITION', description: 'Position on level', example: '05' },
  { key: 'ROW', description: 'Row number', example: 'R01' },
  { key: 'BAY', description: 'Bay number', example: 'B02' },
] as const;

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Warehouse interface matching backend entity
 */
export interface WarehouseBase {
  id: string;
  tenant_id: string;
  code: string;
  name: string;
  description?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  latitude?: number;
  longitude?: number;
  phone?: string;
  email?: string;
  manager_name?: string;
  total_area?: number;
  area_unit?: AreaUnit;
  storage_capacity?: number;
  capacity_unit?: CapacityUnit;
  status: WarehouseStatus;
  is_active: boolean;
  location_code_template: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/**
 * Create warehouse DTO
 */
export interface CreateWarehouseDto {
  code: string;
  name: string;
  description?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  latitude?: number;
  longitude?: number;
  phone?: string;
  email?: string;
  manager_name?: string;
  total_area?: number;
  area_unit?: AreaUnit;
  storage_capacity?: number;
  capacity_unit?: CapacityUnit;
  status?: WarehouseStatus;
  is_active?: boolean;
  location_code_template?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Update warehouse DTO
 */
export type UpdateWarehouseDto = Partial<CreateWarehouseDto>;

/**
 * Zone interface matching backend entity
 */
export interface ZoneBase {
  id: string;
  tenant_id: string;
  warehouse_id: string;
  code: string;
  name: string;
  zone_type: WarehouseZoneType;
  description?: string;
  temperature_min?: number;
  temperature_max?: number;
  temperature_unit?: string;
  humidity_min?: number;
  humidity_max?: number;
  area?: number;
  area_unit?: AreaUnit;
  aisle_count?: number;
  location_count?: number;
  capacity?: number;
  current_capacity?: number;
  is_active: boolean;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/**
 * Create zone DTO
 */
export interface CreateZoneDto {
  warehouse_id: string;
  code: string;
  name: string;
  zone_type: WarehouseZoneType;
  description?: string;
  temperature_min?: number;
  temperature_max?: number;
  temperature_unit?: string;
  humidity_min?: number;
  humidity_max?: number;
  area?: number;
  area_unit?: AreaUnit;
  capacity?: number;
  is_active?: boolean;
}

/**
 * Update zone DTO
 */
export type UpdateZoneDto = Partial<CreateZoneDto>;

// ============================================================================
// STATISTICS TYPES
// ============================================================================

/**
 * Warehouse statistics
 */
export interface WarehouseStatistics {
  total: number;
  active: number;
  operational: number;
  maintenance: number;
  closed: number;
}

/**
 * Capacity utilization levels for UI color coding
 */
export enum CapacityLevel {
  LOW = 'LOW', // 0-50%
  MEDIUM = 'MEDIUM', // 50-75%
  HIGH = 'HIGH', // 75-90%
  CRITICAL = 'CRITICAL', // 90-100%
}

/**
 * Capacity level thresholds
 */
export const CAPACITY_LEVEL_THRESHOLDS = {
  [CapacityLevel.LOW]: { min: 0, max: 50, color: 'green' },
  [CapacityLevel.MEDIUM]: { min: 50, max: 75, color: 'yellow' },
  [CapacityLevel.HIGH]: { min: 75, max: 90, color: 'orange' },
  [CapacityLevel.CRITICAL]: { min: 90, max: 100, color: 'red' },
} as const;

/** Threshold percentage at which capacity is considered near-full (used for critical warnings) */
export const NEAR_FULL_CAPACITY_THRESHOLD = 95;

/** Approximate number of unique inventory items per pallet position (used for rough capacity estimation) */
export const ITEMS_PER_PALLET_ESTIMATE = 10;

/**
 * Get capacity level from percentage
 */
export function getCapacityLevel(percentage: number): CapacityLevel {
  if (percentage < 50) return CapacityLevel.LOW;
  if (percentage < 75) return CapacityLevel.MEDIUM;
  if (percentage < 90) return CapacityLevel.HIGH;
  return CapacityLevel.CRITICAL;
}

/**
 * Get capacity color from percentage
 */
export function getCapacityColor(percentage: number): string {
  return CAPACITY_LEVEL_THRESHOLDS[getCapacityLevel(percentage)].color;
}

/**
 * Warehouse capacity utilization details
 */
export interface WarehouseCapacityUtilization {
  /** Total storage capacity */
  total_capacity: number;
  /** Current used capacity */
  used_capacity: number;
  /** Available capacity */
  available_capacity: number;
  /** Utilization percentage (0-100) */
  utilization_percentage: number;
  /** Capacity unit (pallets, cbm, cft) */
  capacity_unit: CapacityUnit | string;
  /** Capacity level for UI */
  level: CapacityLevel;
  /** Color for UI */
  color: string;
}

/**
 * Warehouse area utilization details
 */
export interface WarehouseAreaUtilization {
  /** Total area */
  total_area: number;
  /** Used area (sum of zone areas) */
  used_area: number;
  /** Available area */
  available_area: number;
  /** Utilization percentage (0-100) */
  utilization_percentage: number;
  /** Area unit (sqm, sqft) */
  area_unit: AreaUnit | string;
  /** Area level for UI */
  level: CapacityLevel;
  /** Color for UI */
  color: string;
}

/**
 * Extended warehouse statistics with capacity information
 */
export interface ExtendedWarehouseStatistics extends WarehouseStatistics {
  /** Capacity utilization details */
  capacity: WarehouseCapacityUtilization | null;
  /** Area utilization details */
  area: WarehouseAreaUtilization | null;
  /** Total zones count */
  zone_count: number;
  /** Total locations count */
  location_count: number;
  /** Occupied locations count */
  occupied_locations: number;
  /** Inventory items count */
  inventory_items: number;
}

/**
 * Single warehouse capacity summary
 */
export interface WarehouseCapacitySummary {
  warehouse_id: string;
  warehouse_code: string;
  warehouse_name: string;
  capacity: WarehouseCapacityUtilization | null;
  area: WarehouseAreaUtilization | null;
  zone_count: number;
  location_count: number;
  occupied_locations: number;
  available_locations: number;
  inventory_items: number;
}

/**
 * Capacity check result for receiving/inventory operations
 */
export interface CapacityCheckResult {
  can_accept: boolean;
  current_utilization: number;
  after_accept_utilization: number;
  available_capacity: number;
  requested_quantity: number;
  capacity_unit: string;
  warnings: string[];
  errors: string[];
}

// ============================================================================
// ZONE CAPACITY & HUMIDITY TYPES
// ============================================================================

/**
 * Zone capacity info for inventory operations
 */
export interface ZoneCapacityInfo {
  zone_id: string;
  zone_code: string;
  zone_name: string;
  zone_type: WarehouseZoneType;
  capacity: number | null;
  current_capacity: number;
  available_capacity: number;
  utilization_percentage: number;
  level: CapacityLevel;
  can_accept: boolean;
}

/**
 * Zone humidity compatibility result
 */
export interface ZoneHumidityMatch {
  zone_id: string;
  zone_code: string;
  zone_name: string;
  zone_type: WarehouseZoneType;
  humidity_min: number | null;
  humidity_max: number | null;
  temperature_min: number | null;
  temperature_max: number | null;
  is_compatible: boolean;
  compatibility_score: number; // 0-100, higher is better
}

/**
 * Product storage requirements for zone matching
 */
export interface ProductStorageRequirements {
  requires_temperature_control: boolean;
  temperature_min?: number;
  temperature_max?: number;
  temperature_unit?: 'C' | 'F';
  requires_humidity_control: boolean;
  humidity_min?: number;
  humidity_max?: number;
  is_hazmat: boolean;
  is_high_value: boolean;
  is_perishable: boolean;
  preferred_zone_types?: WarehouseZoneType[];
  restricted_zone_types?: WarehouseZoneType[];
}

/**
 * Zone selection criteria for putaway/picking
 */
export interface ZoneSelectionCriteria {
  warehouse_id: string;
  product_requirements?: ProductStorageRequirements;
  client_preferred_zones?: string[];
  client_avoid_zones?: string[];
  require_capacity?: number;
  prioritize_available_capacity?: boolean;
}

// ============================================================================
// LOCATION COORDINATE VALIDATION
// ============================================================================

/**
 * Location coordinates to validate against aisle limits
 */
export interface LocationCoordinates {
  level?: number;
  bay?: number;
  position?: number;
  x_coord?: number;
  y_coord?: number;
  z_coord?: number;
}

/**
 * Aisle limits for coordinate validation
 */
export interface AisleLimits {
  levels: number;
  bays: number;
  width?: number;
  length?: number;
  height?: number;
  dimension_unit?: DimensionUnit;
}

/**
 * Coordinate validation result
 */
export interface CoordinateValidationResult {
  valid: boolean;
  warnings: string[];
  errors: string[];
}

/**
 * Validate location coordinates against aisle limits
 *
 * Rule 15: Proper validation at shared layer
 * DRY Compliance: Reusable validation logic for backend and frontend
 *
 * @param coordinates - Location coordinates to validate
 * @param aisleLimits - Aisle dimension limits
 * @returns Validation result with warnings and errors
 *
 * @example
 * const result = validateLocationCoordinates(
 *   { level: 5, bay: 10 },
 *   { levels: 3, bays: 20 }
 * );
 * // result.valid = false (level exceeds max)
 * // result.errors = ['Level 5 exceeds aisle maximum (3)']
 */
export function validateLocationCoordinates(
  coordinates: LocationCoordinates,
  aisleLimits: AisleLimits
): CoordinateValidationResult {
  const warnings: string[] = [];
  const errors: string[] = [];

  // Validate level against aisle levels
  if (coordinates.level !== undefined) {
    if (coordinates.level < 1) {
      errors.push('Level must be at least 1');
    } else if (coordinates.level > aisleLimits.levels) {
      errors.push(`Level ${coordinates.level} exceeds aisle maximum (${aisleLimits.levels})`);
    }
  }

  // Validate bay against aisle bays
  if (coordinates.bay !== undefined) {
    if (coordinates.bay < 1) {
      errors.push('Bay must be at least 1');
    } else if (coordinates.bay > aisleLimits.bays) {
      errors.push(`Bay ${coordinates.bay} exceeds aisle maximum (${aisleLimits.bays})`);
    }
  }

  // Validate position (typically 1 per level-bay intersection, but can vary)
  if (coordinates.position !== undefined && coordinates.position < 1) {
    errors.push('Position must be at least 1');
  }

  // Validate x_coord against aisle width (if provided)
  if (coordinates.x_coord !== undefined && aisleLimits.width !== undefined) {
    const unit = aisleLimits.dimension_unit || '';
    if (coordinates.x_coord < 0) {
      warnings.push('X coordinate is negative');
    } else if (coordinates.x_coord > aisleLimits.width) {
      warnings.push(
        `X coordinate ${coordinates.x_coord} exceeds aisle width (${aisleLimits.width}${unit})`
      );
    }
  }

  // Validate y_coord against aisle length (if provided)
  if (coordinates.y_coord !== undefined && aisleLimits.length !== undefined) {
    const unit = aisleLimits.dimension_unit || '';
    if (coordinates.y_coord < 0) {
      warnings.push('Y coordinate is negative');
    } else if (coordinates.y_coord > aisleLimits.length) {
      warnings.push(
        `Y coordinate ${coordinates.y_coord} exceeds aisle length (${aisleLimits.length}${unit})`
      );
    }
  }

  // Validate z_coord against aisle height (if provided)
  if (coordinates.z_coord !== undefined && aisleLimits.height !== undefined) {
    const unit = aisleLimits.dimension_unit || '';
    if (coordinates.z_coord < 0) {
      warnings.push('Z coordinate is negative');
    } else if (coordinates.z_coord > aisleLimits.height) {
      warnings.push(
        `Z coordinate ${coordinates.z_coord} exceeds aisle height (${aisleLimits.height}${unit})`
      );
    }
  }

  return {
    valid: errors.length === 0,
    warnings,
    errors,
  };
}
